import React, { useEffect, useState } from 'react';
import Head from 'next/head';
import NavBar from '../components/navbar';
import Layout from '../components/layout';
import Container from '../components/container';
import StandingsTable from '../components/standings-table';
import config from '../lib/config';

import { GetStaticProps } from 'next';
import { fetchAPI, fetchData, getPrimaryMenuItems } from '../lib/api';
import { IStandings } from '../models/standings';
import { IPage } from '../models/page';
import { IMenuItem } from '../models/menu-item';
import { ISponsor } from '../models/sponsor';
import { H2 } from '../components/text';

interface IProps {
	primaryMenuItems: IMenuItem[];
	pageData: IPage;
	sponsors: ISponsor[];
	calendars: Option[];
	rankingsData: IStandings[];
}

type Option = {
	name: string;
	id: string;
};

export default function KlassementPage({
	primaryMenuItems,
	pageData: { page },
	sponsors,
	rankingsData,
	calendars,
}: IProps) {
	const pageTitle = `${page.title} | ${config.siteName}`;
	const meta = page.seo?.description || page.title;
	const [standingsData, setStandingsData] = useState<IStandings[]>(rankingsData);
	const [currentYear, setCurrentYear] = useState<Option | null>(calendars[0]);

	useEffect(() => {
		const fetchRoundData = async () => {
			try {
				const response = await fetch(
					`${config.apiUrl}/wp-json/statsperform/v1/season-standings?tmcl=${currentYear?.id}`,
				);
				const data = await response.json();
				setStandingsData(data);
			} catch (error) {
				// handle error
				console.error('Error fetching data:', error);
			}
		};

		if (currentYear && currentYear.id !== calendars[0].id) {
			fetchRoundData();
		} else {
			setStandingsData(rankingsData);
		}
	}, [currentYear, calendars, rankingsData]);

	const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		setCurrentYear({
			id: event.target.value,
			name: event.target.selectedOptions[0].text,
		});
	};

	// Extract the start year from the name property
	const startYear = currentYear ? parseInt(currentYear?.name?.split('/')[0]) : null;

	// Set the boolean value based on the start year
	const isBefore2019 = startYear ? startYear < 2019 : false;

	return (
		<Layout preview sponsors={sponsors} metaDesc={meta} metaTitle={pageTitle}>
			<Head>
				<title>{pageTitle}</title>
			</Head>
			<NavBar items={primaryMenuItems} />
			<Container className="py-12 xl:py-16">
				<article>
					<H2 className="text-center py-6">{page.title}</H2>
					<div className="flex justify-end items-center my-4">
						<div className="flex flex-col items-end">
							<label htmlFor="location" className="block text-base">
								Seizoen
							</label>
							<select
								id="location"
								value={currentYear?.id}
								onChange={handleChange}
								className="mt-2 block  rounded-md border-0 py-1.5 pl-3 pr-10  ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600"
							>
								{calendars.map((option) => (
									<option key={option.id} value={option.id}>
										{option.name}
									</option>
								))}
							</select>
						</div>
					</div>
					<StandingsTable
						clubs={standingsData}
						className="table-fixed sm:table-auto"
						legend={currentYear?.name === '2019/2020' ? false : true}
						showCorornaLegend={currentYear?.name === '2019/2020' ? true : false}
						isHistoric={!!isBefore2019}
						displayPeriodTitle={!!(currentYear && currentYear.id === calendars[0].id)}
						year={currentYear?.name || calendars[0]?.name || ''}
					/>
					<p className="text-black text-sm my-1 mx-3">* 12 punten in mindering</p>
				</article>
			</Container>
		</Layout>
	);
}

export const getStaticProps: GetStaticProps = async ({ preview = false }) => {
	const primaryMenuItems = await getPrimaryMenuItems();
	const pageData = await fetchAPI<{ sponsors: { nodes: any[] } }>(`
		query {
			page(id: 2, idType: DATABASE_ID) {
				title
				content
			}
			sponsors(first: 20) {
				nodes {
					title
					sponsor_fields {
						subtitle
						websiteUrl
						mainSponsor
						invertedImage {
							sourceUrl
						}
					}
				}
			}
		}
	`);

	let rankingsData: IStandings[] | null = null;
	const fetchStandingsData = async () => {
		const endpoint = `${config.apiUrl}/wp-json/statsperform/v1/standings`;
		return fetchData(endpoint);
	};

	try {
		rankingsData = await fetchStandingsData();
	} catch (error) {
		console.error('Error fetching data:', error);
	}

	let calendars: Option[] | null = null;
	const fetchCalenders = async () => {
		const endpoint = `${config.apiUrl}/wp-json/statsperform/v1/available-calendars`;
		return fetchData(endpoint);
	};

	try {
		calendars = await fetchCalenders();
	} catch (error) {
		console.error('Error fetching data:', error);
	}

	return {
		props: {
			sponsors: pageData.sponsors.nodes,
			pageData,
			preview,
			primaryMenuItems,
			calendars,
			rankingsData,
		},
		revalidate: 300,
	};
};
